import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { checkMeiyeAmount } from '$/api/server';
import { useOfficialStore } from '$/store/useOfficialStore';
import { setNeedPayCard } from './useChatBoard';

export type OpenMode = 'topright_page' | 'room_chat' | 'home_page' | 'home_banner' | 'rightside_win' | 'other' | 'index_title' | 'url_purchase' | 'func_runpage' | 'func_token_short' | 'func_lodingpage' | 'workflow_func' | 'team_admin_page';
export type VipModalType = 'subscribe' | 'single';
export type VipMembershipMode = 'personal' | 'team';
export type VipProductEntranceScene = 0 | 1 | 2;
export type VipModalOrderBigData = Record<string, string | number | boolean | undefined | null>;
export type VipModalTrackParams = Record<string, any> & {
  orderBigData?: VipModalOrderBigData;
};

export interface VipModalOpenOptions {
  membershipMode?: VipMembershipMode;
  entranceScene?: VipProductEntranceScene;
}

interface VipModalState {
  open: boolean;
  modalType: VipModalType;
  openMode: OpenMode; // 手动出发订阅
  membershipMode: VipMembershipMode;
  entranceScene: VipProductEntranceScene;
  updateAmountCb: () => void | Promise<unknown>;
  vipErrorMsg: string;
  entranceCode: 'Roboneo_Entrance_Marketing' | 'default'; // 原价｜优惠
  rebateCode: string;
  trackParams: VipModalTrackParams;
}
const vipModalStateState: StateCreator<VipModalState, [
  ['zustand/immer', never],
]> = (_, get) => ({
  open: false,
  modalType: 'subscribe',
  openMode: 'topright_page',
  membershipMode: 'personal',
  entranceScene: 0,
  updateAmountCb: checkMeiyeAmount,
  vipErrorMsg: '',
  entranceCode: 'default',
  rebateCode: '',
  trackParams: {},
});
const useVipModalStore: UseBoundStore<StoreApi<VipModalState>> = create<VipModalState>()(immer(vipModalStateState));
export default useVipModalStore;

/**
 * 未显式指定会员类型时，跟随当前账号上下文定位到个人版或团队版订阅 tab。
 */
function resolveVipMembershipMode(options: VipModalOpenOptions): VipMembershipMode {
  if (options.membershipMode) {
    return options.membershipMode;
  }

  const { clientOId } = useOfficialStore.getState();
  return clientOId ? 'team' : 'personal';
}

/**
 * 判断自动余额不足是否应直接进入单购，个人版最高档会员没有继续升级空间。
 */
function shouldAutoOpenSingleProductModal(): boolean {
  const { clientOId, userInfo } = useOfficialStore.getState();
  const { subscribe } = userInfo;
  const isTeamContext = Boolean(clientOId);

  return !isTeamContext
    && Boolean(subscribe?.isValid)
    && Boolean(subscribe?.isHighestLevel);
}

/**
 * 打开订阅弹窗，并同步入口指定的会员类型与商品场景。
 * @export
 */
export function openVipModal(
  mode: OpenMode = 'topright_page',
  errMsg?: string,
  trackParams: VipModalTrackParams = {},
  options: VipModalOpenOptions = {},
) {
  // 待定，请求参数 控制什么时候发送sse
  useVipModalStore.setState(() => ({
    modalType: 'subscribe',
    openMode: mode,
    membershipMode: resolveVipMembershipMode(options),
    entranceScene: options.entranceScene || 0,
    vipErrorMsg: errMsg,
    trackParams,
    open: true,
  }));
}

/**
 * 打开算力萝卜单购弹窗
 * @export
 */
export function openSingleProductModal(mode: OpenMode = 'topright_page', errMsg?: string, trackParams: VipModalTrackParams = {}) {
  useVipModalStore.setState(() => ({
    modalType: 'single',
    entranceScene: 0,
    openMode: mode,
    vipErrorMsg: errMsg,
    trackParams,
    open: true,
  }));
}

/**
 * 自动余额不足入口：个人最高档会员进入算力萝卜单购，其余用户保持原订阅弹窗逻辑。
 * @export
 */
export function openInsufficientRadishModal(mode: OpenMode = 'func_token_short', errMsg?: string, trackParams: VipModalTrackParams = {}) {
  if (shouldAutoOpenSingleProductModal()) {
    openSingleProductModal(mode, errMsg, trackParams);
    return;
  }

  openVipModal(mode, errMsg, trackParams);
}

/**
 * 切换购买弹窗
 * @export
 */
export function loggleVipModal(visible: boolean) {
  setNeedPayCard(false);
  useVipModalStore.setState(() => ({
    open: visible,
    modalType: visible ? useVipModalStore.getState().modalType : 'subscribe',
    membershipMode: visible ? useVipModalStore.getState().membershipMode : 'personal',
    entranceScene: visible ? useVipModalStore.getState().entranceScene : 0,
    trackParams: visible ? useVipModalStore.getState().trackParams : {},
  }));
}

/**
 * 切换订阅弹窗的个人版/团队版商品模式。
 * @export
 */
export function setVipMembershipMode(membershipMode: VipMembershipMode) {
  useVipModalStore.setState(() => ({ membershipMode }));
}

/**
 * 设置订阅弹窗额外埋点参数，供底层 VipModal 统一透传。
 * @export
 */
export function setVipModalTrackParams(trackParams: VipModalTrackParams) {
  useVipModalStore.setState(() => ({ trackParams }));
}

/**
 * 设置订阅成功后余额回刷回调。
 * @export
 */
export function setAmountCb(cb: () => void | Promise<unknown>) {
  useVipModalStore.setState(() => ({ updateAmountCb: cb }));
}

/**
 * 设置会员订阅商品入口 code。
 * @export
 */
export function setEntranceCode(code: VipModalState['entranceCode']) {
  useVipModalStore.setState(() => ({
    entranceCode: code,
  }));
}

/**
 * 设置会员订阅返利 code。
 * @export
 */
export function setRebateCode(code: string) {
  useVipModalStore.setState(() => ({
    rebateCode: code,
  }));
}
